import { faMoneyBillWave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import cn from 'classnames';
import { useSearchParamsUpdater } from 'hooks/useSearchParamsUpdater';
import { useClickOutside } from 'hooks/useClickOutside';
import { getPaymentByID, updatePayment } from '@redux/features/paymentsSlice';
import { createTransaction } from '@redux/features/transactionsSlice';
import { addNotification } from '@redux/features/notificationsSlice';
import { adjustBalance } from '@redux/features/clientsSlice';
import { formatDate } from 'helpers/formatDate';
import { scrollDate } from 'helpers/scrollDate';
import { MoneyAmount } from 'components/MoneyAmount';
import { DatePicker } from 'components/DatePicker';
import { Checkbox } from 'components/Checkbox';
import { Dropdown } from 'components/Dropdown';
import { Button } from 'components/Button';
import { Input } from 'components/Input';
import styles from './TransactionForm.scss';

export const TransactionForm = ({ payment, entityID, entityType, entityBalance, onClose }) => {
  const { paymentMethods } = useSelector((state) => state.paymentMethods);
  const { locations } = useSelector((state) => state.locations);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [amount, setAmount] = useState(payment.amount - payment.paidAmount);
  const [customDate, setCustomDate] = useState(formatDate(new Date()));
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [isUseBalance, setIsUseBalance] = useState(false);
  const query = useSearchParamsUpdater();
  const { locationName } = useParams();

  const locationTransliteration = query.getParam('location') || locationName;

  const locationID = useMemo(
    () => locations.find((l) => l.transliteration === locationTransliteration)?.id,
    [locations]
  );

  const remainingAmount = useMemo(() => payment.amount - payment.paidAmount, [payment]);
  const dispatch = useDispatch();
  const modalRef = useRef();

  const getPaymentPurpose = (payment) =>
    payment.subscriptionID
      ? `Оплата пакету #${payment.subscriptionID}`
      : payment.entryID
      ? `Оплата заняття #${payment.entryID}`
      : `Оплата рахунку #${payment.id}`;

  const handlePaymentSubmit = useCallback(
    async ({ method, amount }) => {
      if (!isUseBalance && (!method || Number(amount) <= 0 || !locationID)) {
        dispatch(
          addNotification({
            id: Date.now(),
            title: 'Заповніть усі поля',
            type: 'error',
          })
        );
        return;
      }

      let result;
      if (isUseBalance) {
        result = await dispatch(
          adjustBalance({
            description: getPaymentPurpose(payment),
            type: 'payment',
            amount,
            clientID: entityID,
            locationID,
          })
        );

        const newPaidAmount = Number(payment.paidAmount) + Number(amount);

        await dispatch(
          updatePayment({
            id: payment.id,
            paidAmount: newPaidAmount,
            paid: payment.amount === newPaidAmount,
          })
        );
      } else {
        result = await dispatch(
          createTransaction({
            id: payment.id,
            methodID: method.id,
            locationID,
            amount,
            description: getPaymentPurpose(payment),
            type: 'income',
            cashboxID: method.cashbox.id,
            paymentID: payment.id,
            ...(showCustomDate ? { date: customDate } : {}),
            [entityType === 'client' ? 'clientID' : 'coachID']: entityID,
          })
        );
      }

      if (result.payload) {
        dispatch(getPaymentByID({ id: payment.id }));
        onClose();
      }
    },
    [dispatch, payment?.id, isUseBalance, showCustomDate, customDate]
  );

  useClickOutside(modalRef, onClose, true);

  const handleSubmit = (e) => {
    e.preventDefault();
    handlePaymentSubmit({ method: selectedMethod, amount: Number(amount) });
    setSelectedMethod(null);
    setAmount(remainingAmount);
  };

  const paymentMethodsOptions = useMemo(
    () =>
      paymentMethods
        .filter((m) => {
          if (!m?.cashbox?.location) return true;

          return m.cashbox?.location?.transliteration === locationTransliteration;
        })
        .sort((a, b) => {
          if (a.value.startsWith('cash')) return -1;
          if (b.value.startsWith('cash')) return 1;
          return 0;
        }),
    [paymentMethods]
  );

  const handleDateChange = useCallback(
    (direction) => setCustomDate((prev) => scrollDate(prev, direction)),
    [setCustomDate]
  );

  useEffect(() => {
    if (isUseBalance) {
      setAmount(Math.min(entityBalance, remainingAmount));
    } else {
      setAmount(payment.amount - payment.paidAmount);
    }
  }, [isUseBalance, entityBalance, remainingAmount]);

  const oneYearAgoDate = formatDate(new Date(new Date().setFullYear(new Date().getFullYear() - 1)));

  return (
    <div className={styles.modal} ref={modalRef}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
        {entityBalance > 0 && (
          <div className={styles.block}>
            Доступний баланс: <MoneyAmount value={entityBalance} />
            <Checkbox
              className={styles.checkbox}
              checked={isUseBalance}
              onChange={() => setIsUseBalance(() => !isUseBalance)}
              label='Зняти з балансу'
              smallStyle
            />
          </div>
        )}
        <div className={styles.block}>
          <div className={cn(styles.form, { [styles.disabled]: isUseBalance })}>
            <label>Сума оплати</label>
            <div className={styles.inputRow}>
              <Input
                className={cn(styles.input, styles.amount)}
                type='number'
                min={0}
                max={remainingAmount}
                name='amount'
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder='Сума оплати'
              />
              / <MoneyAmount value={remainingAmount} />
            </div>

            <label>Спосіб оплати</label>
            <Dropdown
              className={styles.dropdown}
              options={paymentMethodsOptions}
              value={selectedMethod}
              onChange={setSelectedMethod}
              placeholder='Спосіб оплати'
            />

            <Checkbox
              className={styles.checkbox}
              label='Обрати дату оплати'
              checked={showCustomDate}
              onChange={() => setShowCustomDate((prev) => !prev)}
              smallStyle
            />

            {showCustomDate && (
              <DatePicker
                className={styles.customDate}
                date={customDate}
                minDate={oneYearAgoDate}
                maxDate={formatDate(new Date())}
                handleScrollDate={handleDateChange}
                maxDateTooltip='Оплату не можна провести майбутнім числом'
                enableCalendar
              />
            )}
          </div>
        </div>

        <div className={cn(styles.block, styles.paidInfo)}>
          Внесені кошти:{' '}
          <span className={styles.paidMoney}>
            <MoneyAmount value={payment.paidAmount} /> / <MoneyAmount value={payment.amount} />
          </span>
        </div>

        <Button className={styles.modalButton} type='submit' noStyle>
          <FontAwesomeIcon icon={faMoneyBillWave} />
          Внести оплату
        </Button>
      </form>
    </div>
  );
};
